import { Track } from "@/modules/property-tracks/types";
import { Box, Skeleton, Typography } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import StagesStepper from "../../desktop/property-stage/stages-stepper";
import { useActiveTrackStages } from "@/modules/property-track-settings/hooks/active-track-stages.hook";
import {
  Permission,
  PermissionLevel,
  PermissionsEnum,
} from "@/modules/common/types";
import usePassport from "@/modules/common/hooks/passport";
import { isLevelAllowed } from "@/modules/common/utils";
import { useGetTrackStatusUpdates } from "@/modules/property-tracks/hooks/track-status-updates.hook";
import TrackUserAgent from "./track-user";

export default function TrackCard({
  track,
  sideBarMode = false,
}: Readonly<{
  track: Track;
  sideBarMode?: boolean;
}>) {
  const { data: trackStages, isLoading: isActiveTrackStagesLoading } =
    useActiveTrackStages(track?.trackStage?.trackType?.id);

  const { isLoading: isTrackHistoryUpdatesLoading, stagesPrecedencesHistory } =
    useGetTrackStatusUpdates(track?.uid);

  const { permissions } = usePassport();

  const permission: Permission = {
    name: PermissionsEnum.VIEW_ALL_TRACKS,
    level: PermissionLevel.READ,
  };

  const canViewAllTracks = !!permissions?.some(
    (userPermission) =>
      userPermission.name === permission.name &&
      isLevelAllowed({
        currentLevel: userPermission.level,
        requiredLevel: permission.level,
      })
  );

  const boxProps = canViewAllTracks && {
    component: Link,
    to: `/property-tracks/${track.uid}`,
    target: "_blank",
  };

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.nawy.colors.semantic.layer.primary,
        borderRadius: theme.nawy.radius.lg,
        p: sideBarMode ? 0 : theme.nawy.spacing.md,
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
      })}
    >
      {!sideBarMode && (
        <Typography
          {...boxProps}
          sx={(theme) => ({
            ...theme.nawy.typography.english.variants.body.lg.semibold,
            display: "flex",
            gap: theme.nawy.spacing.xxs,
            color: canViewAllTracks
              ? theme.nawy.colors.semantic.text.accent
              : undefined,
            textDecoration: "none",
            alignItems: "center",
          })}
        >
          {track.trackStage?.trackType.name}
          {canViewAllTracks && <ChevronRight size={18} />}
        </Typography>
      )}

      {isTrackHistoryUpdatesLoading || isActiveTrackStagesLoading ? (
        <Skeleton />
      ) : (
        <StagesStepper
          activeStage={track.trackStage?.precedence}
          trackStages={trackStages}
          stagesPrecedencesHistory={stagesPrecedencesHistory}
        />
      )}
      {track?.user && <TrackUserAgent track={track} />}
    </Box>
  );
}
